import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { onAuthStateChanged } from 'firebase/auth'
import { auth } from '@/services/firebase'
import { useUserStore } from '@/stores/user-store'
import axiosInstance from '@/services/axiosInstance'
import { syncLocaleWithMember } from '@/composables/useLanguage'
import AppLayout from '../layouts/AppLayout.vue'

let authReady: Promise<void> | null = null

function waitForAuth(): Promise<void> {
  if (!authReady) {
    authReady = new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, () => {
        unsubscribe()
        resolve()
      })
    })
  }
  return authReady
}

const routes: Array<RouteRecordRaw> = [
  {
    name: 'login',
    path: '/login',
    component: () => import('../pages/auth/LoginPage.vue'),
    meta: { requiresAuth: false },
  },
  {
    name: 'admin',
    path: '/',
    component: AppLayout,
    redirect: { name: 'dashboard' },
    meta: { requiresAuth: true },
    children: [
      {
        name: 'dashboard',
        path: '/dashboard',
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),
        meta: { allowedRoles: ['ADMIN', 'COACH'] },
      },
      {
        name: 'clients',
        path: 'clients',
        component: () => import('../pages/coach/ClientsPage.vue'),
        meta: { allowedRoles: ['ADMIN', 'COACH'] },
      },
      {
        name: 'client-detail',
        path: 'clients/:id',
        component: () => import('../pages/coach/ClientDetailPage.vue'),
        meta: { allowedRoles: ['ADMIN', 'COACH'] },
      },
      {
        name: 'reports',
        path: 'reports',
        component: () => import('../pages/reports/ReportsPage.vue'),
        meta: { allowedRoles: ['ADMIN', 'COACH'] },
      },
      {
        name: 'report-detail',
        path: 'reports/:id',
        component: () => import('../pages/reports/ReportDetailPage.vue'),
        meta: { allowedRoles: ['ADMIN', 'COACH'] },
      },
      {
        name: 'report-schedules',
        path: 'report-schedules',
        component: () => import('../pages/reports/ReportSchedulesPage.vue'),
        meta: { allowedRoles: ['ADMIN', 'COACH'] },
      },
      {
        name: 'exercise-library',
        path: 'exercise-library',
        component: () => import('../pages/coach/ExerciseLibraryPage.vue'),
        meta: { allowedRoles: ['ADMIN', 'COACH'] },
      },
      {
        name: 'members',
        path: 'members',
        component: () => import('../pages/admin/members/MembersPage.vue'),
        meta: { allowedRoles: ['ADMIN'] },
      },
      {
        name: 'coaches',
        path: 'coaches',
        component: () => import('../pages/admin/coaches/CoachesPage.vue'),
        meta: { allowedRoles: ['ADMIN'] },
      },
      {
        name: 'translations',
        path: 'translations',
        component: () => import('../pages/admin/translations/TranslationsPage.vue'),
        meta: { allowedRoles: ['ADMIN'] },
      },
      {
        name: 'food-database',
        path: 'food-database',
        component: () => import('../pages/admin/food/FoodDatabasePage.vue'),
        meta: { allowedRoles: ['ADMIN'] },
      },
      {
        name: 'exercise-database',
        path: 'exercise-database',
        component: () => import('../pages/admin/exercise/ExerciseDatabasePage.vue'),
        meta: { allowedRoles: ['ADMIN'] },
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('../pages/settings/Settings.vue'),
        // Coaches need this page too — it is where they set their own vacation.
        meta: { allowedRoles: ['ADMIN', 'COACH'] },
      },
    ],
  },
  {
    name: 'print-meal-plan',
    path: '/print/meal-plan/:clientId',
    component: () => import('../pages/print/MealPlanPrintPage.vue'),
  },
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: '404' },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.VITE_REDIRECT_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

router.beforeEach(async (to, _from, next) => {
  const userStore = useUserStore()

  await waitForAuth()

  const firebaseUser = auth.currentUser

  if (firebaseUser && !userStore.isAuthenticated) {
    try {
      const token = await firebaseUser.getIdToken()
      const response = await axiosInstance.get('/api/v1/member/me', {
        headers: { Authorization: `Bearer ${token}` },
      })
      // Session restore on refresh: same reconciliation as sign-in, so a language
      // changed on the phone shows up here without the member touching the picker.
      await syncLocaleWithMember(response.data.locale)
      const roles: string[] = response.data.roles ?? []
      const hasAdmin = roles.includes('ADMIN')
      const hasCoach = roles.includes('COACH') || response.data.isCoach === true
      const effectiveRoles = Array.from(new Set([...roles, ...(hasCoach ? ['COACH'] : [])]))

      if (hasAdmin && hasCoach) {
        // Dual-role user: restore saved panelRole or defer to login page
        const savedRole = localStorage.getItem('panelRole') as 'ADMIN' | 'COACH' | null
        if (savedRole) {
          userStore.setUser(firebaseUser.email || '', firebaseUser.displayName || '', effectiveRoles, savedRole)
        }
        // If no saved role, fall through — LoginPage will show the role picker
      } else if (hasAdmin) {
        userStore.setUser(firebaseUser.email || '', firebaseUser.displayName || '', effectiveRoles, 'ADMIN')
      } else if (hasCoach) {
        userStore.setUser(firebaseUser.email || '', firebaseUser.displayName || '', effectiveRoles, 'COACH')
      } else {
        await auth.signOut()
      }

      // Restore an in-progress "view as coach" session: keep the panel in the
      // coach view so a refresh doesn't snap the admin back to the admin panel.
      if (userStore.isAuthenticated && userStore.hasAdminRole && userStore.actingCoachId != null) {
        userStore.setPanelRole('COACH')
      }
    } catch {
      await auth.signOut()
    }
  }

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth !== false)

  if (!requiresAuth) {
    if (to.name === 'login' && auth.currentUser && userStore.isAuthenticated) {
      next({ name: 'dashboard' })
    } else {
      next()
    }
    return
  }

  if (auth.currentUser && userStore.isAuthenticated) {
    // Check role-based route access
    const allowedRoles = to.meta.allowedRoles as string[] | undefined
    if (allowedRoles && !allowedRoles.includes(userStore.panelRole)) {
      next({ name: 'dashboard' })
      return
    }
    next()
  } else if (auth.currentUser && !userStore.isAuthenticated) {
    // Firebase user exists but not authenticated in store (dual-role user needing selection)
    if (to.name === 'login') {
      next()
    } else {
      next({ name: 'login' })
    }
  } else {
    next({ name: 'login' })
  }
})

export default router
